<template>
    <main :class="$style.page">
        <Typography
            is="h1"
            size="s"
            type="header"
            responsive
        >
            {{ $tr('myGames.title') }}
        </Typography>
        <span :class="$style.inner_sep" />
        <section :class="$style.main_section">
            <template v-if="isMounted">
                <template v-if="myUnzippedGames.length">
                    <div :class="$style.grid">
                        <template
                            v-for="game in myUnzippedGames"
                            :key="game.hru"
                        >
                            <GameCard
                                data-interface-target="game"
                                :game="game"
                                :approximateWidth="280"
                                appearance="medium_horizontal"
                                responsive
                                lazy
                                :targetLink="undefined"
                            />
                        </template>
                    </div>
                </template>
                <div
                    v-else
                    :class="$style.non_content_block"
                >
                    <img
                        :class="$style.img"
                        :src="GhostImg"
                        :alt="$tr('myGames.emptyAlt')"
                    />
                    <Typography
                        is="h1"
                        type="header"
                        size="s"
                    >
                        {{ $tr('myGames.emptyTitle') }}
                    </Typography>
                    <Typography
                        is="p"
                        type="paragraph"
                        size="s"
                        :class="$style.description"
                    >
                        {{ $tr('myGames.emptyDescription') }}
                    </Typography>
                </div>
            </template>
            <div
                v-else
                :class="$style.non_content_block"
            >
                <div :class="$style.img" />
                <Typography
                    is="h1"
                    type="header"
                    size="s"
                >
                    {{ $tr('myGames.waitingTitle') }}
                </Typography>
                <Typography
                    is="p"
                    type="paragraph"
                    size="s"
                    :class="$style.description"
                >
                    {{ $tr('myGames.waitingDescription') }}
                </Typography>
            </div>
        </section>
        <template v-if="userInfoLoaded">
            <LazyHydration :hydrate="hydrateOnIdle()">
                <GameCardCarousel
                    v-if="!isAuthorized"
                    :data-interface-section="currentTrending?.id"
                    :title="currentTrending?.title"
                    :link="currentTrending?.location"
                    :games="unzippedTrendingWithGames"
                    firstItemTabletPlusBig
                    :lastItemLink="currentTrending?.location"
                    :targetLinkGame="undefined"
                />
            </LazyHydration>
        </template>
        <template v-else>
            <GameCardCarousel
                :games="undefined"
                firstItemTabletPlusBig
            />
        </template>
    </main>
</template>
<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import { computed, hydrateOnIdle } from 'vue'

import GameCard from '@/components_new/GameCard/GameCard.vue'
import GameCardCarousel from '@/components_new/GameCardCarousel.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import Typography from '@/components_new/Typography.vue'
import { useUserInfoLoaded } from '@/composables/use-user-state'
import { useTrendingData } from '@/query/use-trending-data'
import { useMyGamesStore } from '@/store/my-games/my-games-store'
import { useUserStore } from '@/store/user-store/user-store'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import GhostImg from './images/ghost-image.webp'

useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] })

const myGamesStore = useMyGamesStore()
const userStore = useUserStore()

const isMounted = useIsMounted()

const { myUnzippedGames } = storeToRefs(myGamesStore)
const { isAuthorized } = storeToRefs(userStore)
const userInfoLoaded = useUserInfoLoaded()

const enableFetchTrending = computed(() => isMounted.value && !isAuthorized.value)
const { unzippedTrendingWithGames, currentTrending } = useTrendingData({
    enabled: enableFetchTrending,
})
</script>
<style module>
.page {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-bottom: 40px;
}
.inner_sep {
    height: 1px;
    display: block;
    background: rgba(255, 255, 255, 0.08);
}

.non_content_block {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: auto;
    max-width: 508px;
}

.img {
    width: 160px;
    height: 160px;
    margin-bottom: 16px;
}

.description {
    color: var(--white-60);
    text-align: center;
    margin-top: 12px;
}
.main_section {
    margin-bottom: 16px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    gap: 8px;
    --cards-gap: 8; /* unitless, consumed by GameCard component */
    position: relative;
}

@media (--tablet-plus) {
    .grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (--desktop-small-plus) {
    .grid {
        grid-template-columns: repeat(4, 1fr);
    }
}

@media (--desktop-medium-plus) {
    .grid {
        grid-template-columns: repeat(6, 1fr);
    }
}
</style>
